/* eslint-disable @typescript-eslint/naming-convention */
import hotkeys from 'hotkeys-js';
import { isBoolean } from 'lodash';
import { getSysType, SYSTEM_TYPE } from '@/utils/platform-kit';
import type { ServiceManager } from '../service-manager';

export interface IHotkeyItem {
    /** 快捷键类型 */
    key: string;
    /** 快捷键名称 */
    name: string;
    /** 快捷键 Windows 菜单显示文本 */
    win: string;
    /** 快捷键 Mac 菜单显示文本 */
    mac: string;
    /** 是否忽略禁用图层选择 */
    ignoreDisablePick?: boolean;
    /** 是否展示在键盘快捷键中 */
    showInKeyboardShortcut?:
        | {
              /** 无限画布模式 */
              edgeless?: boolean;
              /** 普通编辑器模式 */
              normal?: boolean;
          }
        | boolean;
    /** 按键处理器 */
    processors?: {
        /** 快捷键类型 */
        key?: string;
        /** 按键组合 */
        press: string[];
        /** 处理函数 */
        fn: (args?: Record<string, any>) => void;
    }[];
}

export class HotkeyProcessor {
    private __list: IHotkeyItem[] = [];
    private __map: Record<string, IHotkeyItem> = {};
    private __prefix: string;
    private __hotkeyProcessorMap: Record<
        string,
        {
            /** 是否已绑定 */
            isBind: boolean;
            /** 按键组合 */
            pressKey: string;
            /** 处理函数 */
            fn: (e?: Event, args?: Record<string, any>) => void;
            /** 事件处理函数 */
            handler: number;
        }
    > = {};

    /** 是否禁用事件 */
    private __disablePressEvent: boolean = false;

    constructor(readonly service: ServiceManager) {
        this.__prefix = getSysType() === SYSTEM_TYPE.MAC ? 'mac' : 'win';
    }

    processorMount() {
        Object.values(this.__hotkeyProcessorMap).forEach((processor) => {
            if (!processor.isBind) {
                hotkeys(processor.pressKey, processor.fn);
                processor.isBind = true;
            }
        });
    }

    processorUnmount() {
        Object.values(this.__hotkeyProcessorMap).forEach((processor) => {
            if (processor.isBind) {
                hotkeys.unbind(processor.pressKey, processor.fn);
                processor.isBind = false;
            }
        });
        this.__list.length = 0;
        this.__map = {};
        this.__hotkeyProcessorMap = {};
    }

    /** 注册快捷键 */
    register(...items: IHotkeyItem[]) {
        this.__list.push(...items);
        items.forEach((item) => {
            this.__map[item.key] = item;
            item.processors?.forEach(({ key, press, fn }) => {
                const pressKey = press.join(',');
                const itemKey = key ? `${item.key}:${key}` : item.key;
                console.log('processor register ===>', itemKey);
                const processor = this.__hotkeyProcessorMap[itemKey];
                if (processor && processor.isBind) {
                    hotkeys.unbind(pressKey, processor.fn);
                    this.service.events.off(processor.handler);
                }
                const eventName = `${this.__prefix}:${itemKey}`;
                const handler = this.service.events.on(eventName, fn);
                this.__hotkeyProcessorMap[itemKey] = {
                    isBind: false,
                    pressKey,
                    fn: (e?: Event, args?: Record<string, any>) => {
                        e?.preventDefault();
                        if (this._checkDisablePressEvent(item.ignoreDisablePick)) return;
                        this.service.events.emit(eventName, args);
                    },
                    handler,
                };
            });
        });
        this.processorMount();
    }

    /** 监听快捷键事件 */
    on(key: string, fn: (args?: Record<string, any>) => void, weight = 0) {
        const eventName = `${this.__prefix}:${key}`;
        return this.service.events.on(eventName, fn, weight);
    }

    /** 触发自定义快捷键事件 */

    __emit(key: string, args?: Record<string, any>) {
        const item = this.__hotkeyProcessorMap[key];
        if (!item) return;
        item.fn(undefined, args);
    }

    /** 检查快捷键是否存在 */
    exists(key: string) {
        return !!this.__map[key];
    }

    /** 获取快捷键 */
    get(key: string) {
        return this.__map[key];
    }

    /** 获取所有快捷键 */
    getAll() {
        return this.__list.filter(
            (item) =>
                item.showInKeyboardShortcut !== false &&
                (
                    (isBoolean(item.showInKeyboardShortcut) ? {} : item.showInKeyboardShortcut || {}) as {
                        edgeless?: boolean;
                    }
                )?.edgeless !== false &&
                (
                    (isBoolean(item.showInKeyboardShortcut) ? {} : item.showInKeyboardShortcut || {}) as {
                        normal?: boolean;
                    }
                )?.normal !== false,
        );
    }

    /** 获取快捷键描述 */
    getDesc(key: string) {
        const item = this.get(key);
        return item?.[this.__prefix] || '';
    }

    disable() {
        this.__disablePressEvent = true;
    }

    enable() {
        this.__disablePressEvent = false;
    }

    /** 检查是否禁用事件 */
    protected _checkDisablePressEvent(ignoreDisablePick: boolean = false, ignoreReadonly: boolean = false) {
        const { editor } = this.service;
        if (editor.disablePick && !ignoreDisablePick) {
            return true;
        }

        if (this.__disablePressEvent && !ignoreDisablePick) {
            return true;
        }

        if (editor.disableOperate || editor.isMouseSelect || editor.contextMenuVisible) {
            return true;
        }

        if (!ignoreReadonly && editor.readonly) {
            return true;
        }
        return false;
    }
}
